import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getUserProfile,
  updateUserProfile,
} from "../services/user-service";

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 20;

export function validateUsername(value) {
  const name = (value || "").trim();

  if (name.length < USERNAME_MIN) {
    return `Pick a name with at least ${USERNAME_MIN} characters.`;
  }

  if (name.length > USERNAME_MAX) {
    return `Keep it under ${USERNAME_MAX} characters.`;
  }

  return null;
}

export function useProfile(user) {
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return undefined;
    }

    setProfileLoading(true);

    getUserProfile(user.uid)
      .then((loaded) => {
        if (!cancelled) setProfile(loaded);
      })
      .catch((error) => {
        console.error("Could not load profile:", error);
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const saveProfile = useCallback(
    async ({ displayName, avatarId }) => {
      if (!user) return;

      const name = displayName.trim();

      await updateUserProfile(user.uid, { displayName: name, avatarId });

      setProfile((current) => ({
        ...(current || {}),
        displayName: name,
        avatarId,
        profileCompleted: true,
      }));
    },
    [user],
  );

  // profileCompleted is the flag going forward. Accounts created before it
  // existed have no such field, so an avatarId still counts as complete — those
  // users are not dragged back through setup. Everyone else (including accounts
  // seeded manually in the Firebase console) is prompted exactly once.
  const profileCompleted = Boolean(
    profile?.profileCompleted || profile?.avatarId,
  );

  const needsOnboarding =
    Boolean(user) && !profileLoading && !profileCompleted;

  return useMemo(
    () => ({ profile, profileLoading, needsOnboarding, saveProfile }),
    [profile, profileLoading, needsOnboarding, saveProfile],
  );
}

export default useProfile;
